import { useState, useEffect, useCallback } from 'react';
import { styled } from 'styled-components'; // ← Agregar import
import { getInternalServicesReport } from '../services/ReportsService';
import { exportInternalServicesReport } from '../services/ExportService';
import { getWorkTypes } from '../services/OrdersService';
import { searchUsersByName } from '../services/UsersService';
import TableContainer from '../components/table/TableContainer';
import { formatCurrency, formatHours } from '../utils/numbers';
import { formatServerDate } from '../utils/dates';
import { useAppContext } from '../contextApi/context';

const ReportInternalService = () => {
    // ----- Context
    const { loggedUser } = useAppContext();

    // ----- State
    const [workTypes, setWorkTypes] = useState([]);
    const [selectedTypeId, setSelectedTypeId] = useState(null);
    const [filteredSubtypes, setFilteredSubtypes] = useState([]);

    // Estado para autocomplete de mecánicos
    const [availableMechanicsForFilter, setAvailableMechanicsForFilter] = useState([]);
    const [mechanicFilterTimer, setMechanicFilterTimer] = useState(null);

    // ----- API Fetch Actions
    const fetchWorkTypes = useCallback(async () => {
        if (!loggedUser?.token) return;

        try {
            const result = await getWorkTypes(loggedUser.token);
            if (result.success) {
                setWorkTypes(result.data);
            }
        } catch (error) {
            console.log('Error fetching work types:', error);
        }
    }, [loggedUser?.token]);

    // ----- Hooks
    useEffect(() => {
        if (workTypes.length === 0) {
            fetchWorkTypes();
        }
    }, [fetchWorkTypes, workTypes.length]);

    // Actualizar subtipos cuando cambia el tipo seleccionado
    useEffect(() => {
        if (selectedTypeId) {
            const selectedType = workTypes.find((type) => type.id === parseInt(selectedTypeId));
            if (selectedType && selectedType.subtypes && Array.isArray(selectedType.subtypes)) {
                const subtypesForSelectedType = selectedType.subtypes.map((subtype) => ({
                    value: subtype.id,
                    text: subtype.name,
                }));
                setFilteredSubtypes(subtypesForSelectedType);
            } else {
                setFilteredSubtypes([]);
            }
        } else {
            setFilteredSubtypes([]);
        }
    }, [selectedTypeId, workTypes]);

    // Monitorear cambios en el select de tipo en tiempo real
    useEffect(() => {
        const handleSelectChange = (event) => {
            if (event.target.name === 'workType.id') {
                const newValue = event.target.value;
                setSelectedTypeId(newValue || null);
            }
        };

        document.addEventListener('change', handleSelectChange);
        return () => {
            document.removeEventListener('change', handleSelectChange);
        };
    }, []);

    // ----- Actions

    // Función para buscar mecánicos en el filtro
    const handleSearchMechanicsForFilter = useCallback(
        async (searchText) => {
            if (!searchText || searchText.trim() === '') {
                setAvailableMechanicsForFilter([]);
                return;
            }

            clearTimeout(mechanicFilterTimer);

            const newTimer = setTimeout(async () => {
                try {
                    const response = await searchUsersByName(loggedUser.token, searchText);
                    const formattedMechanics =
                        response?.map((user) => ({
                            value: user.id,
                            label: `${user.firstName} ${user.lastName} (${user.username})`,
                        })) || [];
                    setAvailableMechanicsForFilter(formattedMechanics);
                } catch (error) {
                    console.error('Error al buscar mecánicos:', error);
                    setAvailableMechanicsForFilter([]);
                }
            }, 500);

            setMechanicFilterTimer(newTimer);
        },
        [loggedUser, mechanicFilterTimer]
    );

    const handleDataAfterFiltering = (filters) => {
        // Buscar el filtro de tipo de trabajo para sincronizar estado
        const typeFilter = filters.find((f) => f.columnName === 'workType.id');
        if (typeFilter && typeFilter.value && typeFilter.value.length > 0) {
            const newTypeId = typeFilter.value[0];
            if (newTypeId !== selectedTypeId) {
                setSelectedTypeId(newTypeId);
            }
        } else if (!typeFilter || !typeFilter.value || typeFilter.value.length === 0) {
            setSelectedTypeId(null);
        }
    };

    // ----- Constants
    const columns = [
        {
            name: 'N° Orden',
            selector: (row) => row.workOrderNumber,
            sortable: true,
            sortField: 'workOrder.orderNumber',
        },
        {
            name: 'Tipo',
            selector: (row) => row.workTypeName || '--',
            sortable: true,
            sortField: 'workType.name',
        },
        {
            name: 'Subtipo',
            selector: (row) => row.workSubtypeName || '--',
            sortable: true,
            sortField: 'workSubtype.name',
        },
        {
            name: 'Mecánico',
            selector: (row) => row.mechanicName || '--',
            sortable: true,
            sortField: 'mechanic.firstName',
        },
        {
            name: 'Fecha Inicio',
            selector: (row) => formatServerDate(row.startTime),
            sortable: true,
            sortField: 'startTime',
        },
        {
            name: 'Fecha Fin',
            selector: (row) => formatServerDate(row.endTime),
            sortable: true,
            sortField: 'endTime',
        },
        {
            name: 'Horas Trabajadas',
            selector: (row) => formatHours(row.hoursSpent),
            sortable: true,
            sortField: 'hoursSpent',
        },
        {
            name: 'Monto Total',
            selector: (row) => (row.totalAmount ? `₡${formatCurrency(row.totalAmount)}` : '--'),
            sortable: true,
            sortField: 'totalAmount',
        },
        {
            name: 'Descripción',
            cell: (row) => (
                <DescriptionCell data-tooltip={row.description || ''}>
                    {row.description ? (row.description.length > 30 ? `${row.description.substring(0, 30)}...` : row.description) : '--'}
                </DescriptionCell>
            ),
            width: '200px',
        },
    ];

    // Función para generar filtros dinámicos
    const getFilters = () => [
        {
            column: 'workOrder.orderNumber',
            input: 'text',
            label: 'Número de Orden',
            operation: 'like',
            length: 20,
        },
        {
            column: 'workType.id',
            input: 'select',
            label: 'Tipo de Servicio',
            options: workTypes.map((type) => ({
                value: type.id,
                text: type.name,
            })),
            operation: 'like',
        },
        {
            column: 'workSubtype.id',
            input: 'select',
            label: 'Subtipo de Servicio',
            options: selectedTypeId
                ? filteredSubtypes
                : [
                      {
                          value: '',
                          text: 'Seleccione primero un tipo',
                          disabled: true,
                      },
                  ],
            operation: 'like',
            disabled: !selectedTypeId,
        },
        {
            column: 'mechanic.id',
            input: 'autocomplete',
            label: 'Mecánico',
            operation: 'equals',
            placeholder: 'Buscar por nombre, apellido o usuario',
            searchHandler: handleSearchMechanicsForFilter,
            options: availableMechanicsForFilter,
            noOptionsText: 'Escribe para buscar mecánicos',
        },
        {
            column: 'hoursSpent',
            input: 'range',
            label: 'Horas Trabajadas',
            operation: 'between',
        },
        {
            column: 'totalAmount',
            input: 'range',
            label: 'Monto Total',
            operation: 'between',
        },
        {
            column: 'description',
            input: 'text',
            label: 'Descripción',
            operation: 'like',
            length: 50,
        },
        {
            column: 'startTime',
            input: 'dates',
            label: 'Fecha de Inicio',
            operation: 'between',
        },
        {
            column: 'endTime',
            input: 'dates',
            label: 'Fecha de Fin',
            operation: 'between',
        },
    ];

    const exportOptions = {
        handler: exportInternalServicesReport,
    };

    // ----- Render
    return (
        <div className='container mt-5'>
            <h1 className='mb-5'>Reporte de Servicios Internos</h1>

            <TableContainer
                columns={columns}
                fetchDataHandler={getInternalServicesReport}
                updateTable={true}
                filters={getFilters()}
                afterFilteringHandler={handleDataAfterFiltering}
                exportOptions={exportOptions}
            />
        </div>
    );
};

const DescriptionCell = styled.div`
    max-width: 150px;
    position: relative;
    cursor: pointer;

    /* Solo mostrar tooltip si data-tooltip tiene contenido */
    &[data-tooltip]:not([data-tooltip='']):hover::before {
        content: attr(data-tooltip);
        position: absolute;
        bottom: 125%;
        left: 50%;
        transform: translateX(-50%);
        background-color: #333;
        color: #fff;
        padding: 8px 12px;
        border-radius: 6px;
        white-space: normal;
        width: max-content;
        max-width: 300px;
        font-size: 13px;
        font-weight: normal;
        line-height: 1.4;
        z-index: 1000;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
    }

    &[data-tooltip]:not([data-tooltip='']):hover::after {
        content: '';
        position: absolute;
        bottom: 115%;
        left: 50%;
        transform: translateX(-50%);
        border: 6px solid transparent;
        border-top-color: #333;
        z-index: 1000;
    }
`;

export default ReportInternalService;
